import { useEffect, useState } from "react";
import { History, Eye } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { ROLE_LABELS } from "@/lib/strings";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const ACTION_LABELS = {
  CREATE: "Creazione",
  UPDATE: "Modifica",
  DELETE: "Eliminazione",
  STATUS_CHANGE: "Cambio stato",
};

export default function AuditLog() {
  const { companyId, can } = useTenant();
  const [logs, setLogs] = useState([]);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/audit`)
      .then((r) => setLogs(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, [companyId]);

  if (!can("audit:read")) {
    return (
      <div className="max-w-xl" data-testid="audit-forbidden">
        <Card className="border-slate-200"><CardContent className="p-6 text-sm text-slate-600">
          Il tuo ruolo non consente la visualizzazione del registro attività.
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <History className="w-7 h-7 text-green-700" aria-hidden="true" />
          Registro attività (Audit Trail)
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Tracciamento completo delle modifiche rilevanti: azienda, utenti, ruoli e periodi di rendicontazione.
        </p>
      </div>

      {error && <p role="alert" className="text-sm text-red-700" data-testid="audit-error">{error}</p>}

      <Card className="border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table data-testid="audit-log-table">
            <TableHeader>
              <TableRow>
                <TableHead>Data e ora</TableHead>
                <TableHead>Utente</TableHead>
                <TableHead>Ruolo</TableHead>
                <TableHead>Operazione</TableHead>
                <TableHead>Entità</TableHead>
                <TableHead className="text-right">Dettagli</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8" data-testid="audit-empty">
                  Nessuna attività registrata.
                </TableCell></TableRow>
              )}
              {logs.map((l) => (
                <TableRow key={l.id} data-testid={`audit-row-${l.id.slice(0, 8)}`}>
                  <TableCell className="text-sm text-slate-600 whitespace-nowrap">
                    {new Date(l.timestamp).toLocaleString("it-IT")}
                  </TableCell>
                  <TableCell>
                    <p className="text-sm font-medium text-slate-900">{l.user_name || l.user_email}</p>
                    <p className="text-xs text-slate-500">{l.user_email}</p>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-slate-50 text-xs">{ROLE_LABELS[l.user_role] || l.user_role || "—"}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={
                      l.action === "DELETE" ? "bg-red-50 text-red-800 border-red-200" :
                      l.action === "CREATE" ? "bg-green-50 text-green-800 border-green-200" :
                      "bg-sky-50 text-sky-800 border-sky-200"
                    }>{ACTION_LABELS[l.action] || l.action}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">{l.entity}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => setDetail(l)} data-testid={`audit-detail-${l.id.slice(0, 8)}`}>
                      <Eye className="w-4 h-4 mr-1" aria-hidden="true" /> Dettagli
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="audit-detail-dialog">
          <DialogHeader><DialogTitle>Dettaglio attività</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-slate-500 text-xs">Entità</p><p className="font-medium">{detail.entity}</p></div>
                <div><p className="text-slate-500 text-xs">ID entità</p><p className="font-mono text-xs break-all">{detail.entity_id}</p></div>
              </div>
              <div>
                <p className="text-slate-500 text-xs mb-1">Valore precedente</p>
                <pre className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs overflow-auto max-h-40" data-testid="audit-old-value">
                  {detail.old_value ? JSON.stringify(detail.old_value, null, 2) : "—"}
                </pre>
              </div>
              <div>
                <p className="text-slate-500 text-xs mb-1">Nuovo valore</p>
                <pre className="bg-green-50 border border-green-200 rounded-md p-3 text-xs overflow-auto max-h-40" data-testid="audit-new-value">
                  {detail.new_value ? JSON.stringify(detail.new_value, null, 2) : "—"}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
